import { useQuery } from '@tanstack/react-query'
import Loading from 'egret/components/EgretLoadable/Loading'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { queryDescribeObject, queryGetObjects } from './backend/Common'
import { Account, ICCEAccount } from './backend/salesforce-maps/Account'
import { FundingProgram } from './backend/salesforce-maps/FundingProgram'
import { FundingStream } from './backend/salesforce-maps/FundingStream'
import { GranteeBudgetCategoryMap } from './backend/salesforce-maps/GranteeBudgetCategory'
import { Network } from './backend/salesforce-maps/Network'
import { ReportRecordTypesMap } from './backend/salesforce-maps/ReportRecordTypes'
import { mapGranteeBudgetCategories } from './GlobalQueriesUtils'
import { setOrganization } from './redux/actions/OrganizationActions'
import { ReduxState } from './redux/ReduxInterfaces'

interface FormEditorContextType {
  network: Network | undefined
  fundingStreams: FundingStream[]
  fundingPrograms: FundingProgram[]
  fundingProgramsIsLoading: boolean
  refetchFundingStreams: () => Promise<any>
  budgetCategories: GranteeBudgetCategoryMap | object | undefined
  reportRecordTypes: ReportRecordTypesMap | undefined
  availableOrganizations: Account[] | ICCEAccount[]
  refetchAvailableOrganizations: () => Promise<any>
}

const GlobalQueriesContext = React.createContext<FormEditorContextType>({
  network: undefined,
  fundingStreams: [],
  fundingPrograms: [],
  refetchFundingStreams: async () => await Promise.resolve()
})

export function useGlobalQueries (): FormEditorContextType {
  const value = React.useContext(GlobalQueriesContext)
  if (value == null) {
    throw new Error(
      'useGlobalQueries must be wrapped in a <GlobalQueriesContextProvider />'
    )
  }
  return value
}

export function GlobalQueriesContextProvider (
  props: React.PropsWithChildren<FormEditorContextType>
): JSX.Element {
  const userId = useSelector<ReduxState, string>((state) => state.user.userId)
  const currentOrganizationId = useSelector<ReduxState, string>((state) => state.organization.id)
  const dispatch = useDispatch()

  const networkQuery = useQuery<Network>({
    queryKey: ['network'],
    refetchOnWindowFocus: true,
    queryFn: async () =>
      await queryGetObjects({
        type: 'Network',
        select: ['Id', 'Name'],
        searchProps: {
          Name: 'APIConnectionPortal'
        }
      }).then((result) => result[0])
  })

  const fundingStreamsQuery = useQuery<FundingStream[]>({
    queryKey: ['fundingStreams'],
    refetchOnWindowFocus: false,
    queryFn: async () =>
      await queryGetObjects({
        type: 'Funding Stream',
        select: [
          '*',
          'Full_Name__r.*',
          'Funding_Program__r.*',
          'Funding_Program__r.Full_name__r.*',
          'Short_description__r.*',
          'Stream_name_only__r.*',
          'More_info_url__r.*'
        ]
      })
  })

  const fundingProgramsQuery = useQuery<FundingProgram[]>({
    queryKey: ['fundingPrograms'],
    refetchOnWindowFocus: false,
    queryFn: async () =>
      await queryGetObjects({
        type: 'Funding Program',
        select: ['Id', 'Program_overview_form__c', 'Full_name__r.*', 'Name']
      })
  })

  const reportRecordTypesQuery = useQuery<ReportRecordTypesMap>({
    queryKey: ['reportRecordTypes'],
    refetchOnWindowFocus: false,
    queryFn: async () =>
      await queryDescribeObject({
        type: 'Report Record Types'
      }).then((result) => {
        const ret = {}
        for (const item of result.recordTypeInfos) {
          ret[item.developerName] = item
        }
        return ret
      })
  })

  const budgetCategoriesQuery = useQuery<GranteeBudgetCategoryMap>({
    queryKey: ['granteeBudgetCategory'],
    refetchOnWindowFocus: false,
    queryFn: async () =>
      await queryGetObjects({
        type: 'Grantee Budget Category',
        select: ['Id', 'Name', 'FGM_Portal__Parent_Category__c', 'Order__c']
      }).then((results) => mapGranteeBudgetCategories(results))
  })

  const availableAccountsQuery = useQuery<Account[] | ICCEAccount[]>({
    queryKey: ['availableAccounts', userId],
    enabled: userId != null,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const accounts = await queryGetObjects({
        type: 'Account',
        select: [
          'Id',
          'Name',
          'BillingState',
          'RecordType.Name',
          '(SELECT UserId FROM AccountTeamMembers)'
        ]
      })

      const avaliableOrganizations = accounts
        .filter((acc) => {
          return (
            acc.recordType?.name !== 'Household Account' &&
            acc.members?.some((member) => member.UserId === userId)
          )
        })
        .map((org) => ({
          id: org.id,
          province: (org as Account)?.province,
          name: org.organisationsName
        }))

      if (avaliableOrganizations.find(org => org.id === currentOrganizationId) == null) {
        console.warn('Current organization is not in the list of available organizations!', currentOrganizationId)
        dispatch(setOrganization(null))
      }

      return avaliableOrganizations
    }
  })

  // TODO: Add this check only in tabs using funding stream
  if (fundingStreamsQuery.data == null) {
    return <Loading atCenter />
  }

  return (
    <GlobalQueriesContext.Provider
      value={{
        network: networkQuery.data,
        fundingStreams: fundingStreamsQuery.data ?? [],
        refetchFundingStreams: fundingStreamsQuery.refetch,
        fundingPrograms: fundingProgramsQuery.data ?? [],
        fundingProgramsIsLoading: fundingProgramsQuery.isLoading,
        reportRecordTypes: reportRecordTypesQuery.data ?? {},
        budgetCategories: budgetCategoriesQuery.data ?? {},
        availableOrganizations: availableAccountsQuery.data ?? [],
        refetchAvailableOrganizations: availableAccountsQuery.refetch
      }}
    >
      {props.children}
    </GlobalQueriesContext.Provider>
  )
}
